import { useQuery } from '@tanstack/react-query'
import { Search } from 'lucide-react'
import { useEffect, useState } from 'react'

import {
  counterLoanAccountsQueryOptions,
  counterSavingsAccountsQueryOptions,
} from '@/api/teller-api'
import type { CounterLoanAccount, CounterSavingsAccount } from '@/api/teller-types'
import { ErrorSurface } from '@/components/error-surface'
import { MoneyDisplay } from '@/components/money-display'
import { StatusBadge } from '@/components/status-badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Spinner } from '@/components/ui/spinner'
import { cn } from '@/lib/utils'

import { TELLER_PERMISSIONS } from './permissions'

/** Long enough that an account number typed at speed is one request, short enough to feel immediate. */
const SEARCH_DEBOUNCE_MS = 300

/** Ten is what fits without scrolling while somebody is standing at the window. */
const RESULT_LIMIT = 10

function useDebounced(value: string) {
  const [debounced, setDebounced] = useState(value.trim())

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value.trim()), SEARCH_DEBOUNCE_MS)
    return () => window.clearTimeout(timer)
  }, [value])

  return debounced
}

function SearchBox({
  id,
  label,
  hint,
  value,
  onChange,
}: {
  id: string
  label: string
  hint: string
  value: string
  onChange: (value: string) => void
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <div className="relative">
        <Search className="absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted" aria-hidden />
        <Input
          id={id}
          value={value}
          autoComplete="off"
          className="pl-9"
          onChange={(event) => onChange(event.target.value)}
          aria-describedby={`${id}-help`}
        />
      </div>
      <p id={`${id}-help`} className="text-xs text-muted">
        {hint}
      </p>
    </div>
  )
}

/**
 * Finding the savings account a customer is standing at the window about.
 *
 * The search runs against the server, scoped to the selected branch, and it is deliberately a
 * lookup rather than a browse: a teller is confirming one account, not exploring a register.
 *
 * `productClass` narrows the list to the class the chosen rail can actually post to. That is not
 * tidiness — an ordinary deposit against a security deposit account is refused outright with
 * `savings.account_wrong_class`, and the money is already on the counter by then.
 */
export function SavingsAccountPicker({
  branchId,
  productClass,
  selectedId,
  onSelect,
  canView,
}: {
  branchId: string | null
  productClass?: CounterSavingsAccount['productClass']
  selectedId: string | null
  onSelect: (account: CounterSavingsAccount) => void
  canView: boolean
}) {
  const [text, setText] = useState('')
  const debounced = useDebounced(text)

  const query = useQuery(
    counterSavingsAccountsQueryOptions(
      {
        search: debounced === '' ? undefined : debounced,
        productClass,
        branchId: branchId ?? undefined,
        page: 1,
        pageSize: RESULT_LIMIT,
      },
      canView && debounced !== '',
    ),
  )

  if (!canView) {
    return (
      <p className="text-sm text-warning-text">
        Reading savings accounts needs{' '}
        <span className="font-mono text-xs">{TELLER_PERMISSIONS.savingsView}</span>, which your role
        does not hold.
      </p>
    )
  }

  const results = query.data?.items ?? []

  return (
    <div className="space-y-2">
      <SearchBox
        id="counter-savings-search"
        label="Find the account"
        hint="Account number, customer number, or name. A phone number has to be typed in full to match."
        value={text}
        onChange={setText}
      />

      {query.isError ? (
        <ErrorSurface
          error={query.error}
          onRetry={() => void query.refetch()}
          title="The accounts could not be searched"
        />
      ) : (
        <ul className="max-h-64 divide-y divide-border-subtle overflow-y-auto rounded-card border border-border-subtle">
          {debounced === '' ? (
            <li className="p-3 text-xs text-muted">
              Type an account number or a name to search. Nothing is listed until you do — this is a
              counter, not a register.
            </li>
          ) : query.isFetching && results.length === 0 ? (
            <li className="flex items-center gap-2 p-3 text-xs text-muted">
              <Spinner className="size-4" />
              Searching…
            </li>
          ) : results.length === 0 ? (
            <li className="p-3 text-xs text-muted">
              No account at this branch matches
              {productClass === 'SecurityDeposit'
                ? ' among security deposit accounts.'
                : productClass === 'RegularSavings'
                  ? ' among ordinary savings accounts.'
                  : '.'}
            </li>
          ) : (
            results.map((account) => {
              const chosen = selectedId === account.id
              return (
                <li key={account.id}>
                  <button
                    type="button"
                    onClick={() => onSelect(account)}
                    aria-pressed={chosen}
                    className={cn(
                      'flex min-h-11 w-full items-center gap-3 px-3 py-2 text-left transition-colors',
                      chosen ? 'bg-brand-fill-subtle' : 'hover:bg-surface-hover',
                    )}
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-heading">
                        {account.customerName}
                      </span>
                      <span className="block font-mono text-2xs text-muted">
                        {account.accountNumber} · {account.productCode}
                      </span>
                    </span>
                    <StatusBadge status={account.status} />
                    <MoneyDisplay
                      value={account.ledgerBalanceMinor}
                      currency={account.currencyCode}
                      className="w-32"
                    />
                  </button>
                </li>
              )
            })
          )}
        </ul>
      )}
    </div>
  )
}

/**
 * Finding the loan round a collection is being paid against.
 *
 * Only the current round can take a payment, and an account between rounds has none — `currentCycle`
 * is null, and the row says so rather than offering a button that would be refused.
 */
export function LoanAccountPicker({
  branchId,
  selectedId,
  onSelect,
  canView,
}: {
  branchId: string | null
  selectedId: string | null
  onSelect: (account: CounterLoanAccount) => void
  canView: boolean
}) {
  const [text, setText] = useState('')
  const debounced = useDebounced(text)

  const query = useQuery(
    counterLoanAccountsQueryOptions(
      {
        search: debounced === '' ? undefined : debounced,
        branchId: branchId ?? undefined,
        status: 'Active',
        page: 1,
        pageSize: RESULT_LIMIT,
      },
      canView && debounced !== '',
    ),
  )

  if (!canView) {
    return (
      <p className="text-sm text-warning-text">
        Reading loan accounts needs{' '}
        <span className="font-mono text-xs">{TELLER_PERMISSIONS.loansView}</span>, which your role
        does not hold.
      </p>
    )
  }

  const results = query.data?.items ?? []

  return (
    <div className="space-y-2">
      <SearchBox
        id="counter-loan-search"
        label="Find the loan"
        hint="Loan account number, customer number, or name."
        value={text}
        onChange={setText}
      />

      {query.isError ? (
        <ErrorSurface
          error={query.error}
          onRetry={() => void query.refetch()}
          title="The loan accounts could not be searched"
        />
      ) : (
        <ul className="max-h-64 divide-y divide-border-subtle overflow-y-auto rounded-card border border-border-subtle">
          {debounced === '' ? (
            <li className="p-3 text-xs text-muted">Type a loan account number or a name to search.</li>
          ) : query.isFetching && results.length === 0 ? (
            <li className="flex items-center gap-2 p-3 text-xs text-muted">
              <Spinner className="size-4" />
              Searching…
            </li>
          ) : results.length === 0 ? (
            <li className="p-3 text-xs text-muted">No active loan account at this branch matches.</li>
          ) : (
            results.map((account) => {
              const chosen = selectedId === account.id
              const cycle = account.currentCycle

              return (
                <li key={account.id}>
                  <button
                    type="button"
                    onClick={() => onSelect(account)}
                    disabled={cycle === null}
                    aria-pressed={chosen}
                    className={cn(
                      'flex min-h-11 w-full items-center gap-3 px-3 py-2 text-left transition-colors',
                      chosen ? 'bg-brand-fill-subtle' : 'hover:bg-surface-hover',
                      cycle === null && 'cursor-not-allowed opacity-60 hover:bg-transparent',
                    )}
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-heading">
                        {account.customerName}
                      </span>
                      <span className="block font-mono text-2xs text-muted">
                        {account.accountNumber}
                        {cycle ? ` · ${cycle.displayReference}` : ' · between rounds'}
                      </span>
                    </span>
                    {cycle ? <StatusBadge status={cycle.status} /> : null}
                    <MoneyDisplay
                      value={cycle ? cycle.totalOutstandingMinor : null}
                      currency={cycle ? cycle.currencyCode : 'GHS'}
                      className="w-32"
                    />
                  </button>
                </li>
              )
            })
          )}
        </ul>
      )}
    </div>
  )
}
